import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/components/ui/toast"
import { useMutation } from "@tanstack/react-query"
import { Keyboard, Loader2, QrCode } from "lucide-react"
import React, { useState } from "react"
import { customAxios } from "../../../../utils/customAxios/customAxios"
import { getToken } from "../../../../utils/functions/getToken"

type RegisterReaderModalProps = {
  isOpen: boolean
  onClose: () => void
  shopId: string
  onSuccess?: () => void
}

type CreateReaderInput = {
  shopId: string
  registrationCode: string
  label: string
  metadata?: Record<string, string>
}

export const RegisterReaderModal: React.FC<RegisterReaderModalProps> = ({
  isOpen,
  onClose,
  shopId,
  onSuccess,
}) => {
  const [registrationCode, setRegistrationCode] = useState("")
  const [label, setLabel] = useState("")
  const [inputMethod, setInputMethod] = useState<"manual" | "qr">("manual")
  const { addToast } = useToast()

  const registerReaderMutation = useMutation({
    mutationFn: async (data: CreateReaderInput) => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.post(
        "/stripe-terminal/readers",
        data,
        {
          headers: {
            Authorization: token,
          },
        }
      )

      return response.data
    },
    onSuccess: () => {
      addToast({
        type: "success",
        title: "Reader enregistré",
        description: "Le terminal de paiement a été enregistré avec succès",
      })
      handleClose()
      onSuccess?.()
    },
    onError: (error: unknown) => {
      console.error("Erreur lors de l'enregistrement du reader:", error)

      let errorMessage = "Erreur lors de l'enregistrement du terminal"

      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { error?: string; stripe_error_code?: string } }
        }
        if (axiosError.response?.data?.error) {
          errorMessage = axiosError.response.data.error
        } else if (axiosError.response?.data?.stripe_error_code) {
          switch (axiosError.response.data.stripe_error_code) {
            case "resource_missing":
              errorMessage = "Code d'enregistrement invalide ou déjà utilisé"
              break
            case "terminal_reader_invalid_location_type":
              errorMessage = "Type de location invalide pour ce terminal"
              break
            default:
              errorMessage = axiosError.response.data.error || errorMessage
          }
        }
      }

      addToast({
        type: "error",
        title: "Erreur d'enregistrement",
        description: errorMessage,
      })
    },
  })

  const handleClose = () => {
    setRegistrationCode("")
    setLabel("")
    setInputMethod("manual")
    onClose()
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!registrationCode.trim() || !label.trim()) {
      addToast({
        type: "error",
        title: "Champs requis",
        description: "Veuillez remplir tous les champs obligatoires",
      })
      return
    }

    registerReaderMutation.mutate({
      shopId,
      registrationCode: registrationCode.trim(),
      label: label.trim(),
      metadata: {
        registeredAt: new Date().toISOString(),
        inputMethod,
      },
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Enregistrer un terminal de paiement</DialogTitle>
          <DialogDescription>
            Enregistrez un nouveau terminal Stripe Terminal pour ce salon. Vous
            pouvez scanner le QR code du terminal ou saisir le code
            manuellement.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Méthode de saisie */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant={inputMethod === "manual" ? "default" : "outline"}
              size="sm"
              onClick={() => setInputMethod("manual")}
              className="flex items-center gap-2"
            >
              <Keyboard className="h-4 w-4" />
              Saisie manuelle
            </Button>
            <Button
              type="button"
              variant={inputMethod === "qr" ? "default" : "outline"}
              size="sm"
              onClick={() => setInputMethod("qr")}
              className="flex items-center gap-2"
            >
              <QrCode className="h-4 w-4" />
              Scanner QR code
            </Button>
          </div>

          {/* Code d'enregistrement */}
          <div className="space-y-2">
            <Label htmlFor="registrationCode">Code d'enregistrement *</Label>
            <Input
              id="registrationCode"
              placeholder="ex: simulated-wpe ou simulated-s700"
              value={registrationCode}
              onChange={(e) => setRegistrationCode(e.target.value)}
              disabled={registerReaderMutation.isPending}
            />
            <p className="text-xs text-muted-foreground">
              {inputMethod === "qr"
                ? "Scannez le QR code affiché sur votre terminal"
                : "Trouvez ce code dans les paramètres de votre terminal"}
            </p>
          </div>

          {/* Libellé */}
          <div className="space-y-2">
            <Label htmlFor="label">Nom du terminal *</Label>
            <Input
              id="label"
              placeholder="ex: Caisse principale, Terminal accueil..."
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              disabled={registerReaderMutation.isPending}
            />
            <p className="text-xs text-muted-foreground">
              Nom pour identifier ce terminal dans votre salon
            </p>
          </div>

          {/* Aide */}
          <div className="bg-blue-50 p-3 rounded-lg">
            <h4 className="font-medium text-sm mb-2">
              💡 Comment trouver le code ?
            </h4>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• Allumez votre terminal Stripe</li>
              <li>• Allez dans Paramètres → Pairing/Registration</li>
              <li>• Le code s'affiche à l'écran (QR code + texte)</li>
              <li>
                • Pour les tests : utilisez "simulated-wpe" ou "simulated-s700"
              </li>
            </ul>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={registerReaderMutation.isPending}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={registerReaderMutation.isPending}>
              {registerReaderMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Enregistrer le terminal
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
